import type { CuttingStrategy } from './CuttingStrategy';
import type { IMaterial, DebugStep } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';

interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export class DebugStrategy implements CuttingStrategy {
    name = 'Debug Strategy';
    debugSteps: DebugStep[] = [];
    visualizationCounter = 0;
    placedParts: IMaterial[] = [];
    groupedPartsResult: { dimensions: string; count: number; parts: IMaterial[] }[] = [];

    constructor() {
        // No parameters needed for general debug strategy
    }

    optimize(material: IMaterial, parts: IMaterial[]): ICutResult {
        // Clear debug steps
        this.debugSteps = [];
        this.visualizationCounter = 0;
        this.placedParts = [];
        this.groupedPartsResult = [];

        // Group parts by dimensions
        const groupedParts = this.groupPartsByDimensions(parts);
        console.log('Grouped Parts:', groupedParts);

        // Sort parts by area (largest first)
        const sortedParts = [...parts].sort((a, b) => (b.width * b.height) - (a.width * a.height));
        const usedMaterials: IMaterial[] = [];
        const remainingParts = [...sortedParts];

        while (remainingParts.length > 0) {
            const currentMaterial: IMaterial = {
                ...material,
                id: `material-${usedMaterials.length + 1}`,
                children: [] as IMaterial[],
                debugSteps: [] as DebugStep[] // Store debug steps for this material
            };

            // Initialize with one large free rectangle
            const freeRectangles: Rectangle[] = [
                { x: 0, y: 0, width: material.width, height: material.height }
            ];

            // Add initial visualization of free rectangles
            this.addFreeRectanglesVisualization(freeRectangles, "Initial free rectangle");

            let placed = false;
            do {
                placed = false;

                // Try to place each part
                for (let i = 0; i < remainingParts.length; i++) {
                    const part = remainingParts[i];

                    // Try normal orientation first
                    const position = this.findPositionWithDebug(part, freeRectangles);

                    if (position) {
                        // Place the part
                        const placedPart: IMaterial = {
                            ...part,
                            x: position.x,
                            y: position.y,
                            rotated: false
                        };

                        currentMaterial.children!.push(placedPart);

                        // Add debug step for placement
                        this.debugSteps.push({
                            type: 'place',
                            part: placedPart,
                            position: { x: position.x, y: position.y },
                            reason: 'Placed in original orientation'
                        });

                        // Keep track of placed parts
                        this.placedParts.push(placedPart);

                        // Update free rectangles
                        this.splitFreeRectangles(freeRectangles, position, placedPart);

                        // Remove part from remaining list
                        remainingParts.splice(i, 1);
                        placed = true;
                        break;
                    } else {
                        // Add debug step for failed placement in normal orientation
                        this.debugSteps.push({
                            type: 'reject',
                            part: part,
                            reason: 'Could not find suitable position in original orientation'
                        });

                        // Try rotated orientation
                        const rotatedPart = {
                            ...part,
                            width: part.height,
                            height: part.width
                        };

                        // Add debug step for rotation attempt
                        this.debugSteps.push({
                            type: 'rotate',
                            part: part,
                            reason: 'Attempting to rotate part'
                        });

                        const rotatedPosition = this.findPositionWithDebug(rotatedPart, freeRectangles);

                        if (rotatedPosition) {
                            // Place the part in rotated orientation
                            const placedPart: IMaterial = {
                                ...rotatedPart,
                                x: rotatedPosition.x,
                                y: rotatedPosition.y,
                                rotated: true
                            };

                            currentMaterial.children!.push(placedPart);

                            // Add debug step for placement
                            this.debugSteps.push({
                                type: 'place',
                                part: placedPart,
                                position: { x: rotatedPosition.x, y: rotatedPosition.y },
                                reason: 'Placed in rotated orientation'
                            });

                            // Keep track of placed parts
                            this.placedParts.push(placedPart);

                            // Update free rectangles
                            this.splitFreeRectangles(freeRectangles, rotatedPosition, placedPart);

                            // Remove part from remaining list
                            remainingParts.splice(i, 1);
                            placed = true;
                            break;
                        } else {
                            // Add debug step for complete rejection
                            this.debugSteps.push({
                                type: 'reject',
                                part: rotatedPart,
                                reason: 'Could not fit in either orientation'
                            });
                        }
                    }
                }
            } while (placed);

            // Store debug steps for this material
            currentMaterial.debugSteps = [...this.debugSteps];

            // If we placed at least one part
            if (currentMaterial.children!.length > 0) {
                usedMaterials.push(currentMaterial);
            } else {
                // If we couldn't place any parts, break to avoid infinite loop
                break;
            }
        }

        return {
            usedMaterials,
            wastePercentage: this.calculateWaste(material, usedMaterials),
            strategyName: this.name,
            debugSteps: this.debugSteps // Include debug steps in the result
        };
    }

    private findPositionWithDebug(part: IMaterial, freeRectangles: Rectangle[]): Rectangle | null {
        let bestScore = Infinity;
        let bestPosition: Rectangle | null = null;

        for (const rect of freeRectangles) {
            // Check if part fits
            if (rect.width >= part.width && rect.height >= part.height) {
                // Add debug step for checking
                this.debugSteps.push({
                    type: 'check',
                    part: part,
                    position: { x: rect.x, y: rect.y },
                    reason: `Checking fit: ${part.width}x${part.height} in ${rect.width}x${rect.height}`
                });

                // Score using "best short side fit"
                const score = Math.min(
                    rect.width - part.width,
                    rect.height - part.height
                );

                if (score < bestScore) {
                    bestScore = score;
                    bestPosition = {
                        x: rect.x,
                        y: rect.y,
                        width: part.width,
                        height: part.height
                    };
                }
            }
        }

        return bestPosition;
    }

    private splitFreeRectangles(freeRectangles: Rectangle[], placed: Rectangle, placedPart?: IMaterial): void {
        // Create new rectangles based on the placement
        const newRectangles: Rectangle[] = [];

        for (let i = 0; i < freeRectangles.length; i++) {
            const rect = freeRectangles[i];

            // Skip if this free rectangle doesn't intersect with the placed rectangle
            if (rect.x >= placed.x + placed.width || rect.y >= placed.y + placed.height ||
                rect.x + rect.width <= placed.x || rect.y + rect.height <= placed.y) {
                continue;
            }

            // Right of the placed rectangle
            if (rect.x + rect.width > placed.x + placed.width) {
                newRectangles.push({
                    x: placed.x + placed.width,
                    y: rect.y,
                    width: rect.x + rect.width - (placed.x + placed.width),
                    height: rect.height
                });
            }

            // Below the placed rectangle
            if (rect.y + rect.height > placed.y + placed.height) {
                newRectangles.push({
                    x: rect.x,
                    y: placed.y + placed.height,
                    width: rect.width,
                    height: rect.y + rect.height - (placed.y + placed.height)
                });
            }

            // Mark this rectangle for removal
            freeRectangles[i] = { x: -1, y: -1, width: 0, height: 0 };
        }

        // Remove marked rectangles
        for (let i = freeRectangles.length - 1; i >= 0; i--) {
            if (freeRectangles[i].width === 0 || freeRectangles[i].height === 0) {
                freeRectangles.splice(i, 1);
            }
        }

        // Add new rectangles
        freeRectangles.push(...newRectangles);

        // Optimize rectangles by removing contained ones
        this.optimizeFreeRectangles(freeRectangles);

        // Visualize the free rectangles after splitting
        this.addFreeRectanglesVisualization(
            freeRectangles,
            `After placing part at (${placed.x},${placed.y}) of size ${placed.width}x${placed.height}`,
            placedPart
        );
    }

    private addFreeRectanglesVisualization(freeRectangles: Rectangle[], reason: string, placedPart?: IMaterial): void {
        const visualizationId = `freeRect-${++this.visualizationCounter}`;

        this.debugSteps.push({
            type: 'freeRectangles',
            reason,
            freeRectangles: [...freeRectangles], // Clone to prevent further modifications
            placedParts: [...this.placedParts], // Add all placed parts up to this point
            visualizationId
        } as DebugStep);
    }

    private optimizeFreeRectangles(freeRectangles: Rectangle[]): void {
        // Remove rectangles that are contained within others
        for (let i = freeRectangles.length - 1; i >= 0; i--) {
            const rect1 = freeRectangles[i];

            for (let j = 0; j < freeRectangles.length; j++) {
                if (i === j) continue;

                const rect2 = freeRectangles[j];

                if (rect2.x <= rect1.x && rect2.y <= rect1.y &&
                    rect2.x + rect2.width >= rect1.x + rect1.width &&
                    rect2.y + rect2.height >= rect1.y + rect1.height) {

                    freeRectangles.splice(i, 1);
                    break;
                }
            }
        }
    }

    private calculateWaste(originalMaterial: IMaterial, usedMaterials: IMaterial[]): number {
        const totalArea = originalMaterial.width * originalMaterial.height * usedMaterials.length;
        let usedArea = 0;

        usedMaterials.forEach(material => {
            if (material.children) {
                material.children.forEach(part => {
                    usedArea += part.width * part.height;
                });
            }
        });

        return ((totalArea - usedArea) / totalArea) * 100;
    }

    /**
     * Group parts by dimensions and log the grouped result to console
     * @returns Array of grouped parts data
     */
    private groupPartsByDimensions(parts: IMaterial[]): { dimensions: string; count: number; parts: IMaterial[] }[] {
        // Sort parts by size (area) in descending order
        const sortedParts = [...parts].sort((a, b) =>
            (b.width * b.height) - (a.width * a.height)
        );

        const groupedParts: { [key: string]: IMaterial[] } = {};

        // Group sorted parts by width-height combination
        sortedParts.forEach(part => {
            const key = `${part.width}x${part.height}`;
            if (!groupedParts[key]) {
                groupedParts[key] = [];
            }
            groupedParts[key].push(part);
        });

        // Create result array with the grouped data
        this.groupedPartsResult = Object.entries(groupedParts).map(([dimensions, partsList]) => ({
            dimensions,
            count: partsList.length,
            parts: partsList
        }));

        // Log the grouped parts
        console.log('Parts grouped by dimensions (sorted by size):');
        this.groupedPartsResult.forEach(group => {
            console.log(`${group.dimensions} - ${group.count} parts:`);
            console.log(group.parts.map(part => part.id));
        });

        // Also log a summary
        console.log('Dimensions summary:');
        this.groupedPartsResult.forEach(group => {
            console.log(`${group.dimensions}: ${group.count} parts`);
        });

        return this.groupedPartsResult;
    }
}
